/**
 * CalendarFullWidget - Full month calendar view with tasks and events
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { AdaptiveModal } from '../AdaptiveModal';
import { useWindows } from '../../contexts/WindowsContext';
import type { CalendarFullWidgetConfig } from '../../types/widgets';
import { useTodos } from '../../hooks/useTodos';
import { eventsAPI } from '../../services/api';
import type { Todo, Event } from '../../types';
import { TaskStatus } from '../../types';
import { parseLocalTimestamp } from '../../utils/dateUtils';

interface CalendarFullWidgetProps {
  config: CalendarFullWidgetConfig;
  widgetId: string;
  windowId: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function CalendarFullWidget({ config, widgetId, windowId }: CalendarFullWidgetProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { isMobile } = useWindows();
  const { todos } = useTodos();
  const queryClient = useQueryClient();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Compute month range for event fetching
  const monthRange = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start_date: fmt(firstDay), end_date: fmt(lastDay) };
  }, [year, month]);

  // Fetch events for the month
  const { data: monthEvents = [] } = useQuery({
    queryKey: ['events', 'calendar-widget', monthRange.start_date, monthRange.end_date],
    queryFn: () => eventsAPI.getAll({ start_date: monthRange.start_date, end_date: monthRange.end_date }),
  });

  // Get first day of month and number of days
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Build tasks and events per day
  const dayData = useMemo(() => {
    const data: Record<number, { tasks: Todo[]; events: Event[] }> = {};

    for (let d = 1; d <= daysInMonth; d++) {
      data[d] = { tasks: [], events: [] };
    }

    todos.forEach((todo) => {
      const date = todo.deadline;
      if (date) {
        const d = new Date(date);
        if (d.getMonth() === month && d.getFullYear() === year) {
          data[d.getDate()]?.tasks.push(todo);
        }
      }
    });

    monthEvents.forEach((event) => {
      if (event.start_time) {
        const d = parseLocalTimestamp(event.start_time);
        if (d.getMonth() === month && d.getFullYear() === year) {
          data[d.getDate()]?.events.push(event);
        }
      }
    });

    return data;
  }, [todos, monthEvents, month, year, daysInMonth]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => { setCurrentDate(new Date()); setSelectedDay(null); };

  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const monthName = currentDate.toLocaleString('default', { month: 'long' });

  const today = new Date();
  const isToday = (day: number) =>
    today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  const handleDayClick = (day: number) => {
    setSelectedDay(selectedDay === day ? null : day);
  };

  const handleTaskClick = (todo: Todo, e: React.MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo } }));
  };

  const handleEventClick = (event: Event, e: React.MouseEvent) => {
    e.stopPropagation();
    if (event.todo_id) {
      const linkedTodo = todos.find(t => t.id === event.todo_id);
      if (linkedTodo) {
        window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo: linkedTodo } }));
        return;
      }
    }
    window.dispatchEvent(new CustomEvent('openEventFromWidget', { detail: { event } }));
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await eventsAPI.syncGoogleCalendar();
      await queryClient.invalidateQueries({ queryKey: ['events'] });
    } catch {
      // Sync failed silently — events stay as-is
    } finally {
      setIsSyncing(false);
    }
  };

  // Max items to show inline per day cell
  const maxInline = 2;

  const renderCalendarContent = (showSync: boolean) => (
      <div className="p-2 h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {monthName} {year}
            </span>
            <button
              onClick={goToToday}
              className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
            >
              Today
            </button>
            {showSync && (
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                title="Sync Google Calendar"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
          <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px flex-shrink-0">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-[clamp(0.625rem,1.5cqh,0.875rem)] text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid — fills remaining space */}
        <div className="grid grid-cols-7 flex-1 min-h-0 gap-px auto-rows-fr">
          {/* Empty cells */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} />
          ))}

          {/* Days */}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
            const dd = dayData[day];
            const taskCount = dd?.tasks.length || 0;
            const eventCount = dd?.events.length || 0;
            const totalItems = taskCount + eventCount;
            const isSelected = selectedDay === day;

            return (
              <div
                key={day}
                onClick={() => handleDayClick(day)}
                className={`p-0.5 rounded cursor-pointer transition-colors border overflow-hidden ${
                  isToday(day)
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                    : isSelected
                    ? 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                    : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {/* Day number */}
                <div className={`text-[clamp(0.625rem,1.8cqh,0.875rem)] font-medium mb-0.5 px-0.5 ${
                  isToday(day) ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {day}
                </div>

                {/* Inline items — dots on mobile, text on desktop */}
                {isMobile ? (
                  totalItems > 0 && (
                    <div className="flex gap-0.5 flex-wrap px-0.5 mt-0.5">
                      {dd?.events.slice(0, 3).map(event => (
                        <span key={event.id} className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                      ))}
                      {dd?.tasks.slice(0, 3 - eventCount).map(todo => (
                        <span key={todo.id} className={`w-1.5 h-1.5 rounded-full ${
                          todo.status === TaskStatus.COMPLETED ? 'bg-green-500' : 'bg-blue-500'
                        }`} />
                      ))}
                    </div>
                  )
                ) : (
                  <div className="space-y-px overflow-hidden">
                    {dd?.events.slice(0, maxInline).map(event => (
                      <div
                        key={event.id}
                        onClick={(e) => handleEventClick(event, e)}
                        className="text-[clamp(0.5rem,1.4cqh,0.75rem)] leading-tight px-1 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded truncate hover:bg-indigo-200 dark:hover:bg-indigo-800/50"
                        title={`${event.title}${event.start_time ? ` - ${parseLocalTimestamp(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}`}
                      >
                        {event.start_time && (
                          <span className="font-medium">{parseLocalTimestamp(event.start_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} </span>
                        )}
                        {event.title}
                      </div>
                    ))}
                    {dd?.tasks.slice(0, Math.max(0, maxInline - eventCount)).map(todo => (
                      <div
                        key={todo.id}
                        onClick={(e) => handleTaskClick(todo, e)}
                        className={`text-[clamp(0.5rem,1.4cqh,0.75rem)] leading-tight px-1 py-0.5 rounded truncate ${
                          todo.status === TaskStatus.COMPLETED
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 line-through'
                            : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/50'
                        }`}
                        title={todo.title}
                      >
                        {todo.title}
                      </div>
                    ))}
                    {totalItems > maxInline && (
                      <div className="text-[clamp(0.5rem,1.4cqh,0.75rem)] text-gray-400 dark:text-gray-500 px-1">
                        +{totalItems - maxInline} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Selected day detail panel */}
        {selectedDay && dayData[selectedDay] && (
          <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 flex-shrink-0 overflow-auto max-h-[30%]">
            <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              {new Date(year, month, selectedDay).toLocaleDateString('default', { weekday: 'long', month: 'short', day: 'numeric' })}
            </h4>
            {dayData[selectedDay].events.length === 0 && dayData[selectedDay].tasks.length === 0 ? (
              <p className="text-xs text-gray-400">No items</p>
            ) : (
              <div className="space-y-1">
                {dayData[selectedDay].events.map(event => (
                  <div
                    key={event.id}
                    onClick={(e) => handleEventClick(event, e)}
                    className="flex items-center gap-1.5 p-1 rounded bg-indigo-50 dark:bg-indigo-900/20 cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                    <span className="text-xs text-indigo-800 dark:text-indigo-200 truncate">{event.title}</span>
                    {event.start_time && (
                      <span className="text-[10px] text-indigo-500 dark:text-indigo-400 ml-auto whitespace-nowrap">
                        {parseLocalTimestamp(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                ))}
                {dayData[selectedDay].tasks.map(todo => (
                  <div
                    key={todo.id}
                    onClick={(e) => handleTaskClick(todo, e)}
                    className="flex items-center gap-1.5 p-1 rounded bg-white dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      todo.status === TaskStatus.COMPLETED ? 'bg-green-500'
                        : todo.status === TaskStatus.IN_PROGRESS ? 'bg-blue-500'
                        : 'bg-gray-400'
                    }`} />
                    <span className={`text-xs truncate ${todo.status === TaskStatus.COMPLETED ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>
                      {todo.title}
                    </span>
                    {todo.horizon === 'now' && <span className="text-[10px] ml-auto">!</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
  );

  return (
    <>
      <WidgetWrapper title="Calendar" widgetId={widgetId} windowId={windowId} onHeaderClick={() => setExpanded(true)}>
        {renderCalendarContent(false)}
      </WidgetWrapper>
      {expanded && (
        <AdaptiveModal isOpen onClose={() => setExpanded(false)} title="Calendar" size="xl">
          <div className="max-h-[80vh] overflow-auto">{renderCalendarContent(true)}</div>
        </AdaptiveModal>
      )}
    </>
  );
}
